import type { SelectedTimeslotInfo } from 'context/CartContext/types';

/**
 * Formats a timeslot's date & time for display (event page banner + checkout summary).
 *
 * Prefers the full ISO `dateAndTime` so we can render weekday + day + month + time
 * (e.g. "الأربعاء · ٢٥ ديسمبر ١٢:٠٠ ص"). Falls back to the group's formatted date and
 * the slot's raw time string when no ISO datetime is available.
 */
export function formatTimeslotDateTime(
  dateAndTime: string | null | undefined,
  fallbackDate: string | null | undefined,
  fallbackTime: string | null | undefined,
): string {
  if (dateAndTime) {
    const parsed = new Date(dateAndTime);
    if (!Number.isNaN(parsed.getTime())) {
      try {
        return parsed
          .toLocaleString('ar-EG', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          })
          .replace(',', ' ·');
      } catch {
        /* fall through to the raw strings below */
      }
    }
  }
  return [fallbackDate, fallbackTime].filter(Boolean).join(' - ');
}

/**
 * Resolves the date/time string shown on the checkout summary.
 *
 * Uses the selected timeslot when it carries any displayable date/time (so we show
 * the chosen slot, not the parent event). Falls back to the event's own `time`/`date`
 * — including when a slot was selected but has no date/time at all, which avoids
 * rendering a blank line. `filter(Boolean)` drops a null `date`/`time` so we never
 * print the literal "null".
 */
export function formatCheckoutDateTime(
  selectedTimeslot: SelectedTimeslotInfo | undefined,
  fallbackTime: string | null | undefined,
  fallbackDate: string | null | undefined,
): string {
  if (
    selectedTimeslot &&
    (selectedTimeslot.dateAndTime || selectedTimeslot.time || selectedTimeslot.date)
  ) {
    return formatTimeslotDateTime(
      selectedTimeslot.dateAndTime,
      selectedTimeslot.date,
      selectedTimeslot.time,
    );
  }
  return [fallbackTime, fallbackDate].filter(Boolean).join(' ');
}
